// src/contexts/AuthContext.tsx - Sin Firebase, autenticación via API MySQL + JWT
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { resolveEnabledModules, GymModuleId } from '../types/superadmin.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface UserData {
  id: string;
  uid: string; // alias de id para compatibilidad con código Firebase
  email: string;
  name: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'RECEPTIONIST' | 'TRAINER';
  gymId?: string | null;
  isSuperAdmin: boolean;
  avatarUrl?: string | null;
  // Campos de compatibilidad
  emailVerified?: boolean;
  isAnonymous?: boolean;
  reload?: () => Promise<void>;
}

export interface GymData {
  id: string;
  name: string;
  owner?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  logo: string | null;
  isActive: boolean;
  settings?: any;
  // Campos de compatibilidad con código legacy
  status: 'active' | 'trial' | 'suspended';
  totalMembers?: number;
  trialEndsAt?: any;
  loginHistory?: any[];
  kioskSettings?: {
    backgroundUrl?: string;
    enableSounds?: boolean;
  };
  registrationDate?: any;
  cuit?: string;
  subscriptionData?: {
    plan: string;
    startDate: any;
    endDate: any;
    price: number;
    paymentMethod: string;
    lastPayment: any;
    renewalRequested: boolean;
    memberLimit?: number;
    tier?: 'basic' | 'full';
    enabledModules?: GymModuleId[];
  };
}

interface MemberData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dni: string;
  status: string;
  photo?: string;
  memberNumber?: number;
  hasDebt?: boolean;
  totalDebt?: number;
  activeMemberships?: number;
  lastAttendance?: any;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  acceptedPrivacyAt?: string | null;
  acceptedHealthAffidavitAt?: string | null;
  acceptedTermsAt?: string | null;
  acceptedTermsVersion?: string | null;
  allowMarketingImageUse?: boolean;
  digitalSignatureUrl?: string | null;
  isMinor?: boolean;
  tutorName?: string | null;
  tutorDni?: string | null;
  tutorPhone?: string | null;
  tutorRelationship?: string | null;
}

interface AuthContextType {
  currentUser: UserData | null;
  userData: UserData | null;
  memberData: MemberData | null;
  gymData: GymData | null;
  userRole: string | null;
  loading: boolean;
  isSuperAdmin: boolean;
  isGymAdmin: boolean;
  isGymEmployee: boolean;
  isTrialActive: boolean;
  isSubscriptionActive: boolean;
  checkTrialStatus: () => boolean;
  checkSubscriptionStatus: () => boolean;
  subscriptionEndDate: Date | null;
  trialEndDate: Date | null;
  subscriptionDaysLeft: number;
  subscriptionPlan: string | null;
  subscriptionTier: string | null;
  subscriptionStatus: 'active' | 'expired' | 'trial' | 'suspended';
  isLimitReached: boolean;
  memberLimit: number | null;
  enabledModules: GymModuleId[];
  hasModule: (moduleId: GymModuleId) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setRawMemberData?: React.Dispatch<React.SetStateAction<MemberData | null>>;
}


// ─── Helpers API ──────────────────────────────────────────────────────────────

const apiFetch = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errorMsg = data.details ? `${data.error || 'Error'} (${data.details})` : (data.error || `Error ${res.status}`);
    throw new Error(errorMsg);
  }
  return data;
};

const buildGymData = (gym: any): GymData => {
  const rawModules = gym.enabledModules || gym.subscriptionData?.enabledModules || (gym.settings && typeof gym.settings === 'object' ? gym.settings.enabledModules : undefined);
  return {
    id: gym.id,
    name: gym.name,
    owner: gym.name,
    email: gym.email || null,
    phone: gym.phone || null,
    address: gym.address || null,
    logo: gym.logo || null,
    isActive: gym.isActive ?? true,
    settings: gym.settings || {},
    status: (gym.subscriptionStatus || (gym.isActive ? 'active' : 'suspended')) as any,
    kioskSettings: {
      backgroundUrl: gym.kioskBg || gym.settings?.kioskSettings?.backgroundUrl || undefined,
      enableSounds: gym.settings?.kioskSettings?.enableSounds ?? true,
    },
    trialEndsAt: null,
    totalMembers: Number(gym.totalMembers || 0),
    subscriptionData: {
      plan: gym.subscriptionPlanId || 'Plan Estándar',
      startDate: gym.createdAt || new Date(),
      endDate: gym.subscriptionEndDate || null,
      price: 0,
      paymentMethod: 'cash',
      lastPayment: null,
      renewalRequested: false,
      memberLimit: gym.memberLimit !== undefined && gym.memberLimit !== null ? Number(gym.memberLimit) : undefined,
      tier: Array.isArray(rawModules) ? (rawModules.length >= 10 ? 'full' : 'basic') : 'full',
      enabledModules: Array.isArray(rawModules) && rawModules.length > 0 ? rawModules : undefined,
    },
  };
};


const buildUserData = (user: any): UserData => ({
  ...user,
  uid: user.id, // alias para compatibilidad Firebase
  emailVerified: true,
  isAnonymous: false,
  reload: async () => {},
});

// ─── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [rawMemberData, setRawMemberData] = useState<MemberData | null>(null);
  const [gymData, setGymData] = useState<GymData | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const data = await apiFetch('/auth/me');
      if (data.user) setCurrentUser(buildUserData(data.user));
      if (data.member) setRawMemberData(data.member);
      if (data.gym) setGymData(buildGymData(data.gym));
    } catch (e) {
      console.warn('Error refrescando usuario:', e);
    }
  };

  // Al montar: verificar si hay token guardado y restaurar sesión
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    apiFetch('/auth/me')
      .then((data) => {
        setCurrentUser(buildUserData(data.user));
        if (data.member) setRawMemberData(data.member);
        if (data.gym) setGymData(buildGymData(data.gym));
      })
      .catch(() => {
        // Token expirado o inválido
        localStorage.removeItem('token');
        localStorage.removeItem('auth_token');
      })
      .finally(() => setLoading(false));
  }, []);

  // Login via API MySQL
  const login = async (email: string, password: string) => {
    const data = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    localStorage.setItem('token', data.token);
    localStorage.setItem('auth_token', data.token);
    setCurrentUser(buildUserData(data.user));
    if (data.member) setRawMemberData(data.member);
    if (data.gym) setGymData(buildGymData(data.gym));
    return data.user;
  };


  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    setCurrentUser(null);
    setRawMemberData(null);
    setGymData(null);
  };

  // ─── Compatibilidad con código legacy que usa estas props ────────────────

  const userRole = currentUser
    ? currentUser.isSuperAdmin
      ? 'superadmin'
      : currentUser.role?.toLowerCase() || 'admin'
    : null;

  const memberData: MemberData | null = rawMemberData || (currentUser && (userRole === 'member' || currentUser.role?.toLowerCase() === 'member')
    ? {
        id: currentUser.id,
        firstName: currentUser.name.split(' ')[0] || currentUser.name,
        lastName: currentUser.name.split(' ').slice(1).join(' ') || '',
        email: currentUser.email,
        phone: '',
        dni: '',
        status: 'active',
        photo: currentUser.avatarUrl || undefined,
        memberNumber: 1,
        hasDebt: false,
        totalDebt: 0,
      }
    : null);

  const checkTrialStatus = () => false; // Sin trial en sistema MySQL
  const checkSubscriptionStatus = () => !!gymData?.isActive;

  const enabledModules = resolveEnabledModules(
    gymData?.subscriptionData?.enabledModules,
    gymData?.subscriptionData?.tier
  );

  const hasModule = (moduleId: GymModuleId): boolean => {
    if (currentUser?.isSuperAdmin) return true;
    return enabledModules.includes(moduleId);
  };

  const memberLimit = gymData?.subscriptionData?.memberLimit ?? null;
  const totalMembers = gymData?.totalMembers ?? 0;
  const isLimitReached = Boolean(memberLimit && memberLimit > 0 && totalMembers >= memberLimit);

  const value: AuthContextType = {
    currentUser,
    userData: currentUser,
    memberData,
    gymData,
    userRole,

    loading,
    isSuperAdmin: currentUser?.isSuperAdmin ?? false,
    isGymAdmin: userRole === 'admin',
    isGymEmployee: userRole === 'receptionist' || userRole === 'trainer',
    isTrialActive: false,
    isSubscriptionActive: !!gymData?.isActive,
    checkTrialStatus,
    checkSubscriptionStatus,
    subscriptionEndDate: gymData?.subscriptionData?.endDate ? new Date(gymData.subscriptionData.endDate) : null,
    trialEndDate: null,
    subscriptionDaysLeft: 999,
    subscriptionPlan: gymData?.subscriptionData?.plan || 'full',
    subscriptionTier: 'full',
    subscriptionStatus: (gymData?.status as any) || (gymData?.isActive ? 'active' : 'suspended'),
    isLimitReached,
    memberLimit,
    enabledModules,
    hasModule,
    login,
    logout,
    refreshUser,
    setRawMemberData,
  };


  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;